import {
  Combobox,
  ComboboxContent,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
  InputGroupAddon,
} from "@trotroways/ui";
import { cn } from "@trotroways/utils";
import { ArrowUpDown, Loader2, MapPin, Navigation, Clock } from "lucide-react";
import { memo, useCallback, useState } from "react";
import type { Location, SearchResult } from "@/lib/api";
import type { SavedRoute } from "@/lib/saved-routes";

export interface RouteSearchPanelProps {
  fromInput: string;
  toInput: string;
  onFromInputChange: (value: string) => void;
  onToInputChange: (value: string) => void;
  fromSearchResults: SearchResult[];
  toSearchResults: SearchResult[];
  isSearchingFrom: boolean;
  isSearchingTo: boolean;
  onFromSelect: (result: SearchResult) => void;
  onToSelect: (result: SearchResult) => void;
  onUseCurrentLocation: () => void;
  fromLocation: Location | null;
  toLocation: Location | null;
  isLoadingRoutes: boolean;
  onFindRoutes: () => void;
  isFromGps?: boolean;
  onSwap?: () => void;
  onExitGpsMode?: () => void;
  onExpandRequest?: () => void;
  onCollapseRequest?: () => void;
  coverageWarning?: string | null;
  recentSearches?: SavedRoute[];
  onSelectRecentSearch?: (recent: SavedRoute) => void;
}

function InlineLocationInput({
  value,
  onChange,
  results,
  isSearching,
  onSelect,
  placeholder,
  showCurrentLocation,
  onUseCurrentLocation,
  isGps,
  onGpsTap,
  onExpandRequest,
  onCollapseRequest,
}: {
  value: string;
  onChange: (v: string) => void;
  results: SearchResult[];
  isSearching: boolean;
  onSelect: (r: SearchResult) => void;
  placeholder: string;
  showCurrentLocation?: boolean;
  onUseCurrentLocation?: () => void;
  isGps?: boolean;
  onGpsTap?: () => void;
  onExpandRequest?: () => void;
  onCollapseRequest?: () => void;
}) {
  const [open, setOpen] = useState(false);

  const handleValueChange = useCallback(
    (v: unknown) => {
      if (v === "__current__") {
        onUseCurrentLocation?.();
      } else {
        const name = v as string;
        const result = results.find((r) => r.name === name);
        if (result) onSelect(result);
      }
      setOpen(false);
      onCollapseRequest?.();
    },
    [onSelect, onUseCurrentLocation, onCollapseRequest, results],
  );

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      setOpen(nextOpen);
      if (!nextOpen) onCollapseRequest?.();
    },
    [onCollapseRequest],
  );

  const handleFocus = useCallback(() => {
    setOpen(true);
    onExpandRequest?.();
  }, [onExpandRequest]);

  const hasContent = results.length > 0 || isSearching || showCurrentLocation;

  if (isGps) {
    return (
      <button
        type="button"
        onClick={onGpsTap}
        className={cn(
          "flex w-full items-center gap-2 rounded-xl bg-[#0006] px-3 py-2.5 text-white",
          "transition-colors hover:bg-[#0008]",
        )}
      >
        <Navigation className="size-4 text-blue-400" />
        <span className="text-sm font-medium">Current location</span>
      </button>
    );
  }

  return (
    <Combobox
      inputValue={value}
      onInputValueChange={(v) => onChange(v ?? "")}
      open={open && hasContent}
      onOpenChange={handleOpenChange}
      onValueChange={handleValueChange}
      filter={() => true}
    >
      <ComboboxInput
        showTrigger={false}
        showClear={false}
        placeholder={placeholder}
        onFocus={handleFocus}
      >
        <InputGroupAddon>
          <MapPin className="size-4 text-white/40" />
        </InputGroupAddon>
        {isSearching && (
          <InputGroupAddon align="inline-end">
            <Loader2 className="size-4 animate-spin" />
          </InputGroupAddon>
        )}
      </ComboboxInput>

      <ComboboxContent className="max-h-60">
        <ComboboxList>
          {showCurrentLocation && onUseCurrentLocation && !isGps && (
            <ComboboxItem value="__current__">
              <Navigation className="size-3.5 shrink-0" />
              <span className="font-medium">Use current location</span>
            </ComboboxItem>
          )}
          {isSearching && results.length === 0 ? (
            <div className="flex items-center justify-center gap-2 py-4 text-sm text-muted-foreground">
              <Loader2 className="size-3.5 animate-spin" />
              Searching...
            </div>
          ) : results.length > 0 ? (
            results.map((result, index) => (
              <ComboboxItem
                key={`${result.name}-${index}`}
                value={result.name}
              >
                <MapPin className="size-3.5 shrink-0 text-muted-foreground" />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">
                    {result.name}
                  </p>
                  {result.address && (
                    <p className="truncate text-xs text-muted-foreground">
                      {result.address}
                    </p>
                  )}
                </div>
              </ComboboxItem>
            ))
          ) : value.length >= 2 ? (
            <div className="py-4 text-center text-sm text-muted-foreground">
              No locations found.
            </div>
          ) : null}
        </ComboboxList>
      </ComboboxContent>
    </Combobox>
  );
}

export const RouteSearchPanel = memo(function RouteSearchPanel({
  fromInput,
  toInput,
  onFromInputChange,
  onToInputChange,
  fromSearchResults,
  toSearchResults,
  isSearchingFrom,
  isSearchingTo,
  onFromSelect,
  onToSelect,
  onUseCurrentLocation,
  fromLocation,
  toLocation,
  isLoadingRoutes,
  onFindRoutes,
  isFromGps,
  onSwap,
  onExitGpsMode,
  onExpandRequest,
  onCollapseRequest,
  coverageWarning,
  recentSearches,
  onSelectRecentSearch,
}: RouteSearchPanelProps) {
  const handleGpsTap = useCallback(() => {
    onExitGpsMode?.();
  }, [onExitGpsMode]);

  return (
    <div className="space-y-3">
      <div className="space-y-1.5">
        <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-zinc-400">
          From
        </p>
        <InlineLocationInput
          value={fromInput}
          onChange={onFromInputChange}
          results={fromSearchResults}
          isSearching={isSearchingFrom}
          onSelect={onFromSelect}
          placeholder="Move map or search location"
          showCurrentLocation
          onUseCurrentLocation={onUseCurrentLocation}
          isGps={isFromGps}
          onGpsTap={handleGpsTap}
          onExpandRequest={onExpandRequest}
          onCollapseRequest={onCollapseRequest}
        />
      </div>

      {onSwap && (
        <div className="relative flex items-center justify-center -my-1 z-10">
          <button
            type="button"
            onClick={onSwap}
            aria-label="Swap origin and destination"
            className="size-7 rounded-full bg-white/10 border border-white/20 flex items-center justify-center hover:bg-white/20 transition-colors"
          >
            <ArrowUpDown className="size-3.5 text-white/60" />
          </button>
        </div>
      )}

      <div className="space-y-1.5">
        <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-zinc-400">
          To
        </p>
        <InlineLocationInput
          value={toInput}
          onChange={onToInputChange}
          results={toSearchResults}
          isSearching={isSearchingTo}
          onSelect={onToSelect}
          placeholder="Search destination"
          onExpandRequest={onExpandRequest}
          onCollapseRequest={onCollapseRequest}
        />
      </div>

      {coverageWarning && (
        <p className="flex items-start gap-1.5 text-[11px] text-amber-400 -mt-1">
          <MapPin className="size-3 shrink-0 mt-0.5" />
          {coverageWarning}
        </p>
      )}

      <button
        type="button"
        disabled={!fromLocation || !toLocation || isLoadingRoutes}
        onClick={onFindRoutes}
        className={cn(
          "flex w-full items-center justify-center gap-2 rounded-xl py-2.5 text-sm font-semibold",
          "bg-[#2b2b2b]/80 text-white",
          "transition-all duration-200",
          "hover:bg-[#2b2b2b]/50 active:scale-[0.98]",
          "disabled:opacity-40 disabled:pointer-events-none",
        )}
      >
        {isLoadingRoutes && <Loader2 className="size-4 animate-spin" />}
        Find Routes
      </button>

      {recentSearches && recentSearches.length > 0 && onSelectRecentSearch && (
        <div className="space-y-1.5">
          <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-zinc-400">
            <Clock className="size-3" />
            Recent searches
          </p>
          <div className="flex flex-wrap gap-1.5">
            {recentSearches.slice(0, 3).map((recent) => (
              <button
                key={recent.id}
                type="button"
                onClick={() => onSelectRecentSearch(recent)}
                className="max-w-full truncate rounded-full border border-white/15 bg-white/5 px-3 py-1 text-xs text-white/80 transition-colors hover:bg-white/10"
              >
                {recent.fromName} → {recent.toName}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
});
